import { BaseService } from '../api';
/**
 * ShopifyCartService - Service to manage onboarding cart API interactions.
 */
export class ShopifyCartService extends BaseService {
  /**
   * Create a new onboarding session
   * @returns {Promise<Object>} - Response from the backend
   */
  createCart = async () => {
    try {
      const cart = await this.client.post('/api/v4/onboarding/');
      return cart;
    } catch (e) {}
  };

  /**
   * Fetch cart session data
   * @param {string} cartId - The cart ID
   * @returns {Promise<Object>} - Response from the backend
   */
  getCart = async cartId => {
    return await this.client.get(`/api/v4/onboarding/${cartId}/`);
  };

  /**
   * Update cart metadata with pet data
   * @param {string} cartId - The cart ID
   * @param {Object} petData - Pet metadata to update
   * @returns {Promise<Object>} - Response from the backend
   */

  addPetData = async (cartId, petData = {}) => {
    return await this.client.put(`/api/v4/onboarding/${cartId}/pets/`, {
      ...petData,
    });
  };

  /**
   * Attach customer info to the session
   * @param {string} cartId - The cart ID
   * @param {Object} customerData - Customer metadata (name, email)
   * @returns {Promise<Object>} - Response from the backend
   */
  attachCustomer = async (cartId, customerData = {}) => {
    return await this.client.put(
      `/api/v4/onboarding/${cartId}/customers/`,
      customerData,
    );
  };

  /**
   * Remove a pet from the session
   * @param {string} cartId - The cart ID
   * @param {string} petId - The pet ID to remove
   * @returns {Promise<Object>} - Response from the backend
   */
  removePet = async (cartId, petId) => {
    return await this.client.delete(
      `/api/v4/onboarding/${cartId}/pets/${petId}/`,
    );
  };

  /**
   * Upsert a meal plan for a pet in the session
   * @param {string} cartId - The cart ID
   * @param {string} petId - The pet ID to update
   * @param {Object} planData - Meal plan data containing combination_id and optional subscription_cadence
   * @returns {Promise<Object>} - Response from the backend
   */
  upsertMealPlan = async (cartId, petId, planData = {}) => {
    return await this.client.put(
      `/api/v4/onboarding/${cartId}/pets/${petId}/add_plan/`,
      planData,
    );
  };

  /**
   * Attach extras data to the cart
   * @param {string} cartId - The unique identifier for the cart
   * @param {string} petId - The unique identifier for the pet
   * @param {Object} extrasData - Extras metadata (treat/supplement product information)
   * @returns {Promise<Object>} - Response from the backend
   */
  attachExtras = async (cartId, petId, extrasData = []) => {
    return await this.client.put(
      `/api/v4/onboarding/${cartId}/pets/${petId}/add_extras/`,
      extrasData,
    );
  };

  /**
   * Attach a promo code to the session
   * @param {string} cartId - The cart ID
   * @param {string} promoCode - The promotion code to attach
   * @returns {Promise<Object>} - Response from the backend
   */
  addPromo = async (cartId, promoCode) => {
    return await this.client.put(`/api/v4/onboarding/${cartId}/add_promo/`, {
      coupon_code: promoCode,
    });
  };
}

/**
 * ShopifyInventoryService - Service that queries inventory and plan types available in Shopify
 */
export class ShopifyInventoryService extends BaseService {
  /**
   * Retrieve available plan types for the provided pet
   * @param {string} cartId - The unique identifier for the cart
   * @param {string} petId - The unique identifier for the pet
   * @returns {Promise<Object>} - Plan types response payload
   */

  getPlanTypes = async (cartId, petId) => {
    try {
      const res = await this.client.get(
        `/api/v4/onboarding/${cartId}/pets/${petId}/plans/`,
      );
      return res;
    } catch (e) {}
  };
}
